import type { RealtimeCall, RealtimeEvent } from './realtime-call';

/**
 * A call in the core's room (its pipeline, a group's voices): the server's voices are
 * participants of a LiveKit room the caller joins with its token. The caller publishes its
 * microphone and hears each voice (`onRemoteStream`, with the participant saying it); the server
 * tells what is said on the room's data (`topic`), as a GPT-Live call's events, each naming its
 * persona when it is one of a group. The server drives the call: nothing is sent into it.
 */
export async function joinCallRoom(options: {
  url: string;
  token: string;
  /** The data topic the server tells the call's events on. */
  topic: string;
  stream: MediaStream;
  /** Releases what the call holds of its own (a silent stand-in microphone). */
  release(): void;
  signal?: AbortSignal;
  onEvent?: (event: RealtimeEvent) => void;
  onRemoteStream?: (stream: MediaStream, voice: string) => void;
}): Promise<RealtimeCall> {
  const { Room, RoomEvent, Track } = await import('livekit-client');
  const room = new Room();
  let ended = false;
  const teardown = () => {
    if (ended) return;
    ended = true;
    // The microphone is the caller's: it goes on in the call's next session.
    void room.disconnect(false).catch(() => undefined);
    options.release();
  };
  const aborted = () => teardown();
  options.signal?.addEventListener('abort', aborted, { once: true });
  room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
    if (topic !== options.topic) return;
    try {
      const event = JSON.parse(new TextDecoder().decode(payload)) as unknown;
      if (event !== null && typeof event === 'object' && !Array.isArray(event))
        options.onEvent?.(event as RealtimeEvent);
    } catch {}
  });
  room.on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
    if (track.kind !== Track.Kind.Audio) return;
    options.onRemoteStream?.(new MediaStream([track.mediaStreamTrack]), participant.identity);
  });
  room.on(RoomEvent.Disconnected, () => {
    if (!ended) options.onEvent?.({ type: 'session.closed' });
    teardown();
  });
  try {
    await room.connect(options.url, options.token, { autoSubscribe: true });
    const [microphone] = options.stream.getAudioTracks();
    if (microphone !== undefined)
      await room.localParticipant.publishTrack(microphone, { source: Track.Source.Microphone });
    if (options.signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    options.onEvent?.({ type: 'session.started' });
    return {
      sessionId: null,
      transport: 'room',
      protocol: 'v1',
      send: () => !ended,
      close: async () => teardown(),
    };
  } catch (error) {
    teardown();
    throw error;
  } finally {
    options.signal?.removeEventListener('abort', aborted);
  }
}
